// src/Layout/Sidebar.js/Sidebar.js
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { SIDEBAR_ACCESS, ROUTE_MODULES, canAccessModule, hasUIAccess } from '../../config/rbacConfig';
import { useModuleCatalog } from '../../config/moduleCatalog';
import NlimsLogo from '../../assest/img/LOGO.png';
import IConOne from '../../assest/svg/Executive.svg';
import IconTwo from '../../assest/svg/Geospatial.svg';
import IconThree from '../../assest/svg/LivestockGroup.svg';
import IconFour from '../../assest/svg/Market.svg';
import IconFive from '../../assest/svg/Infrastructure.svg';
import IconSix from '../../assest/svg/Workforce.svg';
import IconSeven from '../../assest/svg/Planning.svg';
import IconEight from '../../assest/svg/Conflict.svg';
import IconNine from '../../assest/svg/AnimalIdentity.svg';
import IconTen from '../../assest/svg/AnimalHealth.svg';
import IconEleven from '../../assest/svg/TrustCenter.svg';
import IconTwelve from '../../assest/svg/RegistryManagement.svg';

const menuItems = [
  {
    category: 'Dashboard',
    items: [
      {
        name: 'Executive Overview',
        path: '/executive-overview',
        icon: IConOne,
        labelByRole: { R05: 'Field Dashboard', R04: 'State Dashboard' },
      },
      {
        name: 'Geospatial Overview',
        path: '/geospatial-overview',
        icon: IconTwo,
        hiddenForRoles: ['R05'],
      },
    ],
  },
  {
    category: 'Core Modules',
    items: [
      { name: 'Livestock Population', moduleId: 'livestock-population', path: '/livestock-population', icon: IconThree },
      { name: 'Markets & Trade Intelligence', moduleId: 'markets-trade', path: '/markets-trade', icon: IconFour },
      { name: 'Infrastructure & Services', moduleId: 'infrastructure', path: '/infrastructure', icon: IconFive },
      { name: 'Workforce & Institutional Actors', moduleId: 'workforce-institutional', path: '/workforce-institutional', icon: IconSix },
      { name: 'Planning & Reporting (PAR)', moduleId: 'planning-reporting', path: '/planning-reporting', icon: IconSeven },
      { name: 'Conflicts & Dispute Records', moduleId: 'conflicts-dispute', path: '/conflicts-dispute', icon: IconEight },
    ],
  },
  {
    category: 'Integrated Modules',
    items: [
      { name: 'Animal Identification', moduleId: 'animal-identification', path: '/animal-identification', icon: IconNine },
      { name: 'Animal Health', moduleId: 'animal-health', path: '/animal-health', icon: IconTen },
    ],
  },
  {
    category: 'System Management',
    items: [
      { name: 'Admin & Security', path: '/admin', icon: IconEleven },
      { name: 'Registry Management', path: '/registry-management', icon: IconTwelve },
    ],
  },
];

const Sidebar = ({
  mobileOpen = false,
  onCloseMobile = () => {},
  collapsed = false,
  onToggleCollapsed = () => {},
}) => {
  const { user, permissions } = useAuth();
  const { catalog } = useModuleCatalog();

  const allowedCategories = user?.roleId ? SIDEBAR_ACCESS[user.roleId] ?? [] : [];
  const visibleMenu = hasUIAccess(user?.roleId)
    ? menuItems
        .filter((section) => allowedCategories.includes(section.category))
        .map((section) => ({
          ...section,
          items: section.items
            .filter((item) => {
              if (item.hiddenForRoles?.includes(user?.roleId)) return false;
              const moduleKey = ROUTE_MODULES[item.path];
              return moduleKey ? canAccessModule(user, permissions, moduleKey) : true;
            })
            .map((item) => {
              const catalogName = item.moduleId
                ? catalog.find((module) => module.id === item.moduleId)?.name
                : null;
              return {
                ...item,
                displayName: item.labelByRole?.[user?.roleId] || catalogName || item.name,
              };
            }),
        }))
        .filter((section) => section.items.length > 0)
    : [];

  const showExpandedText = mobileOpen || !collapsed;

  return (
    <aside
      id="app-sidebar"
      className={`
        fixed inset-y-0 left-0 z-50 flex h-screen flex-col bg-[#0a0a0a] text-white shadow-2xl
        w-[min(86vw,300px)]
        transition-all duration-300 ease-in-out shrink-0
        ${mobileOpen ? 'translate-x-0 pointer-events-auto' : '-translate-x-full pointer-events-none'}
        lg:relative lg:translate-x-0 lg:pointer-events-auto lg:shadow-none
        ${collapsed ? 'lg:w-[68px]' : 'lg:w-[300px]'}
      `}
    >
      <div className="flex items-center justify-between border-b border-white/8 px-3 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 min-w-[28px] shrink-0 items-center justify-center rounded-lg bg-white">
            <img src={NlimsLogo} alt="NLIMS" className="h-full w-full rounded-lg object-contain" />
          </div>

          {showExpandedText && (
            <div className="overflow-hidden">
              <p className="whitespace-nowrap text-sm font-bold leading-tight text-white">
                NLIMS
              </p>
              <p className="mt-0.5 text-[10px] leading-tight text-gray-400">
                National Livestock Information Management System
              </p>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={mobileOpen ? onCloseMobile : onToggleCollapsed}
          className="
            ml-3 flex h-7 w-7 items-center justify-center rounded-full
            border border-white/15 bg-[#0a0a0a] text-gray-400 transition-all duration-200
            hover:border-white/30 hover:text-white
          "
          title={mobileOpen ? 'Close' : (collapsed ? 'Expand' : 'Collapse')}
          aria-label={mobileOpen ? 'Close sidebar' : (collapsed ? 'Expand sidebar' : 'Collapse sidebar')}
        >
          {mobileOpen ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className={`transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          )}
        </button>
      </div>

      <nav
        className="flex-1 overflow-y-auto py-3 sidebar-scroll"
        style={{ scrollbarGutter: 'stable' }}
      >
        {visibleMenu.map((section) => (
          <div key={section.category} className="mb-5">
            {showExpandedText ? (
              <p className="mb-1.5 px-4 text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                {section.category}
              </p>
            ) : (
              <div className="mx-3 mb-1.5 h-px bg-white/8" />
            )}

            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    title={!showExpandedText ? item.displayName : undefined}
                    onClick={() => {
                      if (mobileOpen) onCloseMobile();
                    }}
                    className={({ isActive }) => `
                      relative mx-2 flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-all duration-150 group
                      ${isActive
                        ? 'bg-green-500/12 font-semibold text-green-400'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                      }
                    `}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-green-400" />
                        )}

                        <img
                          src={item.icon}
                          alt=""
                          className={`h-[18px] w-[18px] min-w-[18px] transition-all duration-150
                            ${isActive
                              ? 'brightness-0 saturate-100 invert-0 [filter:invert(58%)_sepia(98%)_saturate(400%)_hue-rotate(90deg)_brightness(95%)]'
                              : 'opacity-55 group-hover:opacity-85'
                            }
                          `}
                        />

                        {showExpandedText && (
                          <span className="truncate leading-snug">{item.displayName}</span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;
